import React, { useState, useEffect, useMemo } from 'react';
import head from 'lodash/head';
import last from 'lodash/last';
import isEqual from 'lodash/isEqual';
import groupBy from 'lodash/groupBy';
import keys from 'lodash/keys';
import reduce from 'lodash/reduce';
import PropTypes from 'prop-types';
import {
    Table,
    TableBody,
    TableCell,
    TableHeaderCell,
    TableHeaderCellSortable,
    TableRow,
} from 'smartsuite-ui';
import useSortable from '../../../hooks/useSortable';
import renderTypes from '../../renderTypes';

function useFormattedResults(results, groupKey, fixedColumns, nonFixedColumns) {
    const [formattedResults, setFormattedResults] = useState(results);

    useEffect(() => {
        const fixedColumn = head(fixedColumns);
        const nonFixedColumnDataKeys = nonFixedColumns.map((x) => x.dataKey);

        const groupedResults = groupBy(
            results,
            fixedColumn ? fixedColumn.dataKey : ''
        );
        const formattedData = reduce(
            groupedResults,
            (result, rowData, rowKey) => {
                const updatedRowData = {};
                rowData.forEach((dataItem) => {
                    nonFixedColumnDataKeys.forEach((dataKey) => {
                        updatedRowData[`${dataItem[groupKey]};${dataKey}`] =
                            dataItem[dataKey];
                    });
                });
                result.push({
                    [fixedColumn.dataKey]: rowKey,
                    ...updatedRowData,
                });
                return result;
            },
            []
        );
        setFormattedResults(formattedData);
    }, [results, groupKey, fixedColumns, nonFixedColumns]);

    return formattedResults;
}

function duplicateNonFixedColumnConfigs(
    groupValues,
    fixedColumns,
    nonFixedColumns
) {
    return groupValues.reduce(
        (result, groupValue) => {
            result.push(
                ...nonFixedColumns.map((nonFixedColumn) => ({
                    ...nonFixedColumn,
                    dataKey: `${groupValue};${nonFixedColumn.dataKey}`,
                }))
            );
            return result;
        },
        [...fixedColumns]
    );
}

function getInitialColumnConfigurations(configuration, data) {
    const columns = [...configuration.columns];
    const fixedColumns = last(columns).filter((x) => x.isFixed);
    const nonFixedColumns = last(columns).filter((x) => !x.isFixed);

    let groupValues = null;
    if (configuration.groupKey) {
        const groupedData = groupBy(data, configuration.groupKey);
        groupValues = keys(groupedData);
        columns[columns.length - 1] = duplicateNonFixedColumnConfigs(
            groupValues,
            fixedColumns,
            nonFixedColumns
        );
    }

    return { columns, groupValues, fixedColumns, nonFixedColumns };
}

function GroupColumnTable({
    configuration,
    data,
    legendFormatter,
    formatValue,
}) {
    const { columns, groupValues, fixedColumns, nonFixedColumns } = useMemo(
        () => getInitialColumnConfigurations(configuration, data),
        [configuration, data]
    );

    const formattedResults = useFormattedResults(
        data,
        configuration.groupKey,
        fixedColumns,
        nonFixedColumns
    );

    const [sortObject, setSort, sortedResults] = useSortable(
        formattedResults,
        configuration.initialSortObject
    );

    const renderGroupHeaderParentColumns = () => {
        const groupHeaderColumns = groupValues.sort();
        const colspan = nonFixedColumns.length;
        const scope = colspan > 1 ? 'colgroup' : 'col';

        return (
            <>
                {groupHeaderColumns.map((groupHeaderColumn) => (
                    <TableHeaderCell
                        key={groupHeaderColumn}
                        colSpan={colspan}
                        scope={scope}
                    >
                        {groupHeaderColumn}
                    </TableHeaderCell>
                ))}
            </>
        );
    };

    const renderHeaderColumn = (columnConfig, columnIndex) => {
        const { colspan, dataKey, isFixed, renderType } = columnConfig;
        const scope = colspan > 1 ? 'colgroup' : 'col';

        if (renderType === renderTypes.groupHeader) {
            return renderGroupHeaderParentColumns();
        }

        if (renderType === renderTypes.placeholder) {
            return <TableHeaderCell colSpan={colspan} scope={scope} />;
        }

        if (renderType === renderTypes.staticParentHeader) {
            return (
                <TableHeaderCell colSpan={colspan} scope={scope}>
                    {legendFormatter(dataKey, null, columnIndex)}
                </TableHeaderCell>
            );
        }

        const { 1: columnName } = dataKey.split(';');
        return (
            <TableHeaderCellSortable
                key={dataKey}
                columnName={dataKey}
                sortObject={sortObject}
                onSort={setSort}
            >
                {isFixed
                    ? legendFormatter(dataKey, null, columnIndex)
                    : legendFormatter(columnName, null, columnIndex)}
            </TableHeaderCellSortable>
        );
    };

    const renderHeaderRow = (headerRowColumns) => (
        <TableRow>{headerRowColumns.map(renderHeaderColumn)}</TableRow>
    );

    const renderCell = (resultRow, columnConfig) => (
        <TableCell>{formatValue(resultRow[columnConfig.dataKey])}</TableCell>
    );

    const renderDataRow = (resultRow) => {
        const columnConfigs = last(columns);

        return (
            <TableRow>
                {columnConfigs.map((columnConfig) =>
                    renderCell(resultRow, columnConfig)
                )}
            </TableRow>
        );
    };

    return (
        <div className="widget__table-container">
            <Table>
                <thead>{columns.map(renderHeaderRow)}</thead>
                <TableBody>{sortedResults.map(renderDataRow)}</TableBody>
            </Table>
        </div>
    );
}

GroupColumnTable.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    data: PropTypes.instanceOf(Array).isRequired,
    formatValue: PropTypes.func,
    legendFormatter: PropTypes.func,
};

GroupColumnTable.defaultProps = {
    formatValue: (x) => x,
    legendFormatter: (x) => x,
};

export default React.memo(GroupColumnTable, (prevProps, nextProps) =>
    isEqual(prevProps.data, nextProps.data)
);
